import { Component, computed, DestroyRef, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateDirective } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map } from 'rxjs';

import { IDENTITY_MENU_PARAMS, IdentityClientService, IdentityConfigurationService, IdentityService } from '@auth';
import { SasNavItemComponent } from '@components';
import { SharedConstants } from '@constants';
import { EventService } from '@core';
import { OrderByPipe } from '@pipes';
import { SasMenuService } from './sas-menu.service';

import { MENU_ICONS } from './sas-menu.icons';

@Component({
    selector: 'sas-menu',
    imports: [TranslateDirective, OrderByPipe, SasNavItemComponent],
    templateUrl: './sas-menu.component.html',
    styleUrl: './sas-menu.component.scss'
})
export class SasMenuComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly eventService = inject(EventService);
    private readonly identityClientService = inject(IdentityClientService);
    private readonly identityConfigService = inject(IdentityConfigurationService);
    private readonly identityService = inject(IdentityService);
    private readonly router = inject(Router);
    private readonly menuParams = inject(IDENTITY_MENU_PARAMS);
    protected readonly menuService = inject(SasMenuService);

    readonly clientUrl = computed(() => this.identityClientService.get().url);
    readonly serviceUrl = signal(this.identityConfigService.getServiceUrl());
    readonly hasLandpage = !this.identityConfigService.getRootModule();

    private resolveIcon(code: string): string {
        return MENU_ICONS[code.trim()] ?? 'bi-columns-gap';
    }

    protected readonly landpageMenuOption = {
        code: 'LANDPAGE',
        external: false,
        index: 0,
        value: 'landpage',
        icon: MENU_ICONS['landpage'] ?? 'bi-columns-gap',
    };
    protected readonly menuOptions = signal<any[]>([]);
    private readonly currentModule = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => this.extractModule(event.urlAfterRedirects)),
            distinctUntilChanged()
        ),
        { initialValue: '' }
    );
    readonly SIGN_OUT_CODE = signal('SIGN_OUT');
    readonly SIGN_IN_CODE = signal('SIGN_IN');
    readonly tabIndex = computed(() => this.menuService.hidden() ? -1 : 0);
    readonly hasMoreToScroll = signal(false);
    private readonly navScrollArea = viewChild<ElementRef<HTMLElement>>('navScrollArea');

    private recalculateScrollHint(): void {
        const el = this.navScrollArea()?.nativeElement;
        if (el) {this.hasMoreToScroll.set(el.scrollHeight > el.clientHeight + 4);}
    }

    onNavScroll(el: HTMLElement): void {
        this.hasMoreToScroll.set(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    }

    scrollToBottom(): void {
        this.navScrollArea()?.nativeElement.scrollTo({ top: Number.MAX_SAFE_INTEGER, behavior: 'smooth' });
    }

    constructor() {
        // Show menu + load options when module resolver fires
        this.eventService.getEvent(SharedConstants.RELOAD_MENU_EVENT)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // currentModule lags during navigation, so read the target URL; the landpage never shows the menu.
                const nav = this.router.currentNavigation();
                const url = nav?.finalUrl ? this.router.serializeUrl(nav.finalUrl) : this.router.url;
                if (this.extractModule(url.split(/[?#]/)[0]) !== 'landpage') {
                    this.menuService.show();
                }
                this.loadMenu();
            });

        // Hide menu on landpage
        effect(() => {
            const module = this.currentModule();
            if (module === 'landpage') {
                this.menuService.hide();
            }
        });

        // Close mobile drawer on every navigation
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.menuService.closeMobile());

        // ResizeObserver sobre el wrapper: dispara post-layout en colapso/expansión
        // (el wrapper cambia de tamaño cuando el brand crece/decrece)
        effect((onCleanup) => {
            const el = this.navScrollArea()?.nativeElement;
            if (!el) {return;}
            const observer = new ResizeObserver(() => this.recalculateScrollHint());
            observer.observe(el.parentElement!); // observa el wrapper
            onCleanup(() => observer.disconnect());
        });
    }

    private extractModule(url: string): string {
        const segments = url.split('/').filter(Boolean);

        // segments[0] = client, segments[1] = service
        return segments[1] || '';
    }

    private loadMenu() {
        this.serviceUrl.set(this.identityConfigService.getServiceUrl());
        this.identityService.getMenu(this.menuParams).subscribe((menuOptions: any[]) => {
            this.menuOptions.set(this.mapMenuOptions(menuOptions));
            // Recalculate scroll hint after items render
            setTimeout(() => this.recalculateScrollHint());
        });
    }

    private mapMenuOptions(items: any[]): any[] {
        return items.map((item: any) => ({
            index: item.index,
            code: item.code.trim(),
            value: item.value,
            external: item.external,
            icon: this.resolveIcon(item.code),
        }));
    }

    toggleCollapse() {
        this.menuService.toggle();
    }

    isMenuActive(opt: any): boolean {
        const current = this.router.url.toLowerCase();
        if (!current) {return false;}

        const normalized = opt.value?.replace(/[A-Z]/g, (m: string) => `-${m.toLowerCase()}`).toLowerCase();

        return new RegExp(`(^|/)${normalized}(/|$|\\?)`).test(current);
    }

    protected getItemRouterLink(option: any): string[] | null {
        if (option.external) {return null;}
        const client = this.clientUrl();
        if (option.code === this.SIGN_IN_CODE()) {return [`/${client}/${option.value}`];}
        const service = this.serviceUrl();
        const path = service ? `/${client}/${service}/${option.value}` : `/${client}/${option.value}`;
        return [path];
    }
}
